import type {ReactElement} from 'react'
import React from 'react'
import Eye from '../../icons/eye'
import {Field} from 'formik'
interface Props {
  type: string
  name: string
  label: string
  placeholder: string
  isError: any
  error: any | undefined
  autofocus?: boolean
  dir?: 'ltr' | 'rtl'
}
export default function TextInput({
  type,
  name,
  label,
  isError,
  error,
  placeholder,
  autofocus = false,
  dir = 'rtl',
}: Props): ReactElement {
  const [showPass, setShowPass] = React.useState(false)
  const isRequired = label.includes('*')
  const labelText = isRequired ? label.replace('*', '') : label

  return (
    <>
      <div className="w-full">
        <label
          htmlFor={name}
          className="w-full text-right block text-xs text-primary">
          {labelText}
          {isRequired && <span className="text-red-600 text-lg font-bold leading-none">*</span>}
        </label>
        <div className="relative">
          <Field
            className={`w-full placeholder:font-light placeholder:text-[#93A3B0CC] placeholder:opacity-80 mt-2 py-3 px-3 focus:ring-1 transition duration-200 focus:ring-black focus:border-transparent text-xs
                                rounded-md ${
                                  isError
                                    ? 'border-1 border-red'
                                    : 'border-1 border-gray-300'
                                } `}
            autoFocus={autofocus}
            id={name}
            dir={dir}
            name={name}
            type={showPass ? 'text' : type}
            placeholder={placeholder}
            inputMode={['id_number', 'cvv'].includes(name) ? 'numeric' : undefined}
            pattern={name === 'cardHolderName' || name === 'full_name' ? '[A-Za-z\u0600-\u06FF\u0590-\u05FF ]+' : (['id_number', 'cvv'].includes(name) ? '[0-9]*' : undefined)}
            onInput={
              name === 'cardHolderName' || name === 'full_name'
                ? (e: React.ChangeEvent<HTMLInputElement>) => {
                    e.target.value = e.target.value.replace(/[^A-Za-z\u0600-\u06FF\u0590-\u05FF ]/g, '');
                  }
                : ['id_number', 'cvv'].includes(name)
                  ? (e: React.ChangeEvent<HTMLInputElement>) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '');
                    }
                  : undefined
            }
          />
          {type === 'password' && (
            <a
              href="#"
              onClick={(e: any) => {
                e.preventDefault()
                setShowPass(!showPass)
              }}
              className="absolute left-3 top-5">
              <Eye />
            </a>
          )}
        </div>
      </div>
      <div className=" mt-1 text-right text-sm text-red font-semibold">
        {error}
      </div>
    </>
  )
}
interface PaymentProps extends Props {
  values: any
}
export function CreditCardInput({
  type,
  name,
  label,
  isError,
  error,
  placeholder,
  values,
}: PaymentProps): ReactElement {
  const isRequired = label.includes('*')
  const labelText = isRequired ? label.replace('*', '') : label

  return (
    <>
      <div className="w-full">
        <label htmlFor={name} className="w-full text-right block text-xs">
          {labelText}
          {isRequired && <span className="text-red-600 text-lg font-bold leading-none">*</span>}
        </label>
        <div className="relative">
          <Field
            dir="ltr"
            className={`bg-athens-500 w-full
                          text-right      placeholder:text-bombay-500 mt-2 py-3 px-3 focus:ring-1 transition
                                duration-200 focus:ring-black focus:border-transparent text-xs
                                rounded-md ${
                                  isError ? 'border-1 border-red' : 'border-0'
                                } `}
            id={name}
            value={values.creditCard
              .replace(/\W/gi, '')
              .replace(/(.{4})/g, '$1 ')
              .substring(0, 19)}
            name={name}
            type={type}
            placeholder={placeholder}
            inputMode="numeric"
            onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
              // Only allow digits, then format with spaces
              const digits = e.target.value.replace(/[^0-9]/g, '');
              e.target.value = digits;
            }}
          />
        </div>
      </div>

      {isError && (
        <div className=" mt-1 text-right text-sm text-red-500 font-semibold">
          {error}
        </div>
      )}
    </>
  )
}

export function ExpiryInput({
  type,
  name,
  label,
  isError,
  error,
  placeholder,
  values,
}: PaymentProps): ReactElement {
  const isRequired = label.includes('*')
  const labelText = isRequired ? label.replace('*', '') : label
  
  // Parse the current expiry value
  const parts = values.expiry ? values.expiry.split('/') : ['', ''];
  const currentMonth = parts[0] || '';
  const currentYear = parts[1] || '';
  
  // Generate month options (01-12)
  const months = Array.from({ length: 12 }, (_, i) => {
    const monthNum = i + 1;
    return monthNum < 10 ? `0${monthNum}` : `${monthNum}`;
  });
  
  // Generate year options (current year + 10 years)
  const currentFullYear = new Date().getFullYear();
  const years = Array.from({ length: 11 }, (_, i) => {
    return `${currentFullYear + i}`.slice(-2); // Take last 2 digits
  });

  return (
    <>
      <div className="w-full">
        <label htmlFor={name} className="w-full text-right block text-xs">
          {labelText}
          {isRequired && <span className="text-red-600 text-lg font-bold leading-none">*</span>}
        </label>
        
        <div className="flex space-x-2 rtl:space-x-reverse">
          {/* Using a wrapper div for Field to preserve the layout */}
          <div className="w-1/2">
            <Field name={name}>
              {({ field, form }: any) => (
                <select
                  className={`bg-athens-500 w-full
                              text-center placeholder:text-bombay-500 mt-2 py-3 px-3 focus:ring-1 transition
                              duration-200 focus:ring-black focus:border-transparent text-xs
                              rounded-md ${
                                isError ? 'border-1 border-red' : 'border-0'
                              } `}
                  value={currentMonth}
                  onChange={(e) => {
                    const newValue = `${e.target.value}/${currentYear}`;
                    form.setFieldValue(name, newValue);
                  }}
                >
                  <option value="" disabled>MM</option>
                  {months.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              )}
            </Field>
          </div>
          
          <div className="w-1/2">
            <Field name={name}>
              {({ field, form }: any) => (
                <select
                  className={`bg-athens-500 w-full
                              text-center placeholder:text-bombay-500 mt-2 py-3 px-3 focus:ring-1 transition
                              duration-200 focus:ring-black focus:border-transparent text-xs
                              rounded-md ${
                                isError ? 'border-1 border-red' : 'border-0'
                              } `}
                  value={currentYear}
                  onChange={(e) => {
                    const newValue = `${currentMonth}/${e.target.value}`;
                    form.setFieldValue(name, newValue);
                  }}
                >
                  <option value="" disabled>YY</option>
                  {years.map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              )}
            </Field>
          </div>
        </div>
      </div>

      {isError && (
        <div className="mt-1 text-right text-sm text-red-500 font-semibold">
          {error}
        </div>
      )}
    </>
  )
}
